import { useEffect, useMemo, useRef } from 'react';
import type { DeckProps, Layer, PickingInfo } from '@deck.gl/core';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Map as MapLibreMap } from 'maplibre-gl';

type MapLibreDeckOverlayProps = {
  map: MapLibreMap | null;
  layers: Layer[];
  onHover?: (info: PickingInfo) => void;
  onClick?: (info: PickingInfo) => void;
};

const PICKING_RADIUS_PIXELS = 14;

export const MapLibreDeckOverlay = ({ map, layers, onHover, onClick }: MapLibreDeckOverlayProps) => {
  const overlayRef = useRef<MapboxOverlay | null>(null);
  const overlayAddedRef = useRef(false);
  const loadHandlerRef = useRef<(() => void) | null>(null);

  const overlayProps = useMemo<DeckProps>(() => {
    const devicePixelRatio = map?.getPixelRatio?.() ?? window.devicePixelRatio ?? 1;
    return {
      layers,
      onHover,
      onClick,
      _animate: true,
      useDevicePixels: devicePixelRatio,
      pickingRadius: PICKING_RADIUS_PIXELS,
      parameters: {
        depthTest: false
      }
    };
  }, [layers, map, onHover, onClick]);

  useEffect(() => {
    if (!map) {
      if (overlayRef.current) {
        overlayRef.current.setProps({ layers: [] });
      }
      overlayAddedRef.current = false;
      return;
    }

    if (!overlayRef.current) {
      overlayRef.current = new MapboxOverlay(overlayProps);
    }

    const ensureAdded = () => {
      if (!overlayRef.current || overlayAddedRef.current) return;
      try {
        map.addControl(overlayRef.current);
        overlayAddedRef.current = true;
      } catch {
        // Ignore add errors if map isn't ready yet.
      }
    };

    if (map.isStyleLoaded()) {
      ensureAdded();
    } else {
      const handleLoad = () => {
        ensureAdded();
      };
      loadHandlerRef.current = handleLoad;
      map.once('load', handleLoad);
    }

    return () => {
      if (loadHandlerRef.current) {
        map.off('load', loadHandlerRef.current);
        loadHandlerRef.current = null;
      }
      if (overlayRef.current) {
        if (overlayAddedRef.current) {
          try {
            map.removeControl(overlayRef.current);
          } catch {
            // Ignore removal errors when map is already disposed.
          }
        }
        overlayAddedRef.current = false;
        overlayRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!overlayRef.current) return;
    overlayRef.current.setProps(overlayProps);
  }, [overlayProps]);

  useEffect(() => {
    if (!map) return;
    const handleRender = () => {
      overlayRef.current?.setProps({ _animate: true });
    };
    map.on('render', handleRender);
    return () => {
      map.off('render', handleRender);
    };
  }, [map]);

  return null;
};
